import React, { useEffect, useState, useRef, useMemo } from 'react';
import MainLayout from '../components/MainLayout';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { useNavigate } from 'react-router-dom';
import { apiUrls } from '../utils/apiUrls';
import { callAPI } from '../utils/apiUtils';
import { FinalizeOrderCartItemModal } from '../components/modals/FinalizeOrderCartItemModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { resetOrderSession } from '../utils/resetOrderSession';
import useCart from '../hooks/useCart';



const FinalizeOrder = () => {
  const navigate = useNavigate();
  const searchInputRef = useRef(null);


  const [seatingTabs, setSeatingTabs] = useState([]);
  const [tableData, setTableData] = useState({});
  const [activeTab, setActiveTab] = useState('');
  const [search, setSearch] = useState('');
  const [occupiedTables, setOccupiedTables] = useState({});
  const [selectedTables, setSelectedTables] = useState({});
  const [cartModal, setCartModal] = useState({ open: false, tableId: null, tableName: '' });
  const { resetCart } = useCart(undefined, null, { skipInitialFetch: true });


    useEffect(() => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/');
        return;
      }
      localStorage.removeItem('reduxSelectedTables');
      localStorage.removeItem('selectedTables');
      resetOrderSession();
      resetCart();
      setSelectedTables({});
    }, []);
  

  useEffect(() => {
    async function fetchTabs() {
      const res = await callAPI(apiUrls.TABLE_DATA, 'GET');

      const seatingTabs = res.data.data.seatingTabs || {};
      const tableData = res.data.data.tableData || {};

      const DEFAULT_TAB = {
        id: "default-tab",
        label: "Default",
        icon: "/images/sprite.svg#iconTab"
      };

      let cleanedTabs = [];
      let cleanedTableData = {};


      cleanedTableData["default-tab"] = [];


      Object.entries(seatingTabs).forEach(([key, tab]) => {

        const tabId = tab.id;

        // A pager is already "finalised" the moment it's paid (Prepaid at the counter) - it has
        // no unpaid balance left to settle here, and it's released with a single tap on the
        // dashboard instead (see useTableOrdering.js's handlePagerTap). Excluding it here also
        // avoids this page's own separate isSpecialTab list (below) rendering it with the wrong
        // - physical-table - tile styling, since it was never taught about pager-tab.
        if (tabId === 'pager-tab') {
          return;
        }

        if (!tabId) {
          cleanedTableData["default-tab"].push(...(tableData[key] || []));
          return;
        }


        if (tableData[tabId] && tableData[tabId].length > 0) {
          cleanedTabs.push(tab);
          cleanedTableData[tabId] = tableData[tabId];
        }
      });


      if (cleanedTableData["default-tab"].length > 0) {
        cleanedTabs.push(DEFAULT_TAB);
      }


      setSeatingTabs(cleanedTabs);
      setTableData(cleanedTableData);


      if (cleanedTabs.length > 0) {
        setActiveTab(cleanedTabs[0].id);
      }
    }

    fetchTabs();
  }, []);




  useEffect(() => {
    async function fetchOrders() {
      const res = await callAPI(apiUrls.OCCUPIED_TABLE_WITH_ORDERS, 'GET', null, {}, {}, false);
      const list = res.data.data || [];

      const map = {};
      list.forEach(t => {
        map[t.id] = {
          started: t.ordered_started_at,
          amount: parseFloat(t.order_amount || 0).toFixed(2),
          customerName: t.customer?.name || null,
          orderName: t.customer?.order_name?.trim() || null,
        };
      });

      setOccupiedTables(map);
    }
    fetchOrders();
  }, []);



  const getElapsed = (startTime) => {
    if (!startTime) return '';
    const start = new Date(startTime);
    const sec = Math.floor((new Date() - start) / 1000);
    return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m ${sec % 60}s`;
  };


  const handleTableSelect = (tabId, tableId) => {
    const updated = { [tabId]: [tableId] };
    setSelectedTables(updated);

    localStorage.setItem('selectedTables', JSON.stringify(updated));

    navigate(`/payment-view?table_id=${tableId}`);
  };

  const handleInfoClick = (tableId, tableName) => {
    setCartModal({ open: true, tableId, tableName });
  };

  const printKitchenDocket = async (tableId, type) => {
    const formData = new FormData();
    formData.append("table_id", tableId);
    formData.append("type", type);

    try {
      const res = await callAPI(apiUrls.PRINT_KITCHEN_DOCKET, {}, 'POST', formData,{});
      toast.success(res.data.message || "Print sent successfully!");
    } catch (err) {
      console.log(err)
      toast.error("Print did not send!");
    }
  };

  const filteredTableData = useMemo(() => {
    const filtered = {};

    seatingTabs.forEach(tab => {
      const tables = tableData[tab.id] || [];

      filtered[tab.id] = tables.filter(t => {
        if (!occupiedTables[t.id]) return false;

        if (!search.trim()) return true;

        const query = search.toLowerCase();
        const tableName = (t.name || `${t.tableNumber}`).toLowerCase();

        return (
          tableName.includes(query) ||
          t.tableNumber?.toString().includes(query) ||
          t.personNumber?.toString().includes(query)
        );
      });
    });

    return filtered;
  }, [seatingTabs, tableData, occupiedTables, search]);

  const handleSearch = (e) => setSearch(e.target.value);

  useEffect(() => {
    if (!search.trim()) return;

    const firstTabWithResults = seatingTabs.find(
      tab => (filteredTableData[tab.id] || []).length > 0
    );

    if (firstTabWithResults) {
      setActiveTab(firstTabWithResults.id);
      setTimeout(() => {
        searchInputRef.current?.focus();
        searchInputRef.current?.setSelectionRange(search.length, search.length);
      }, 0);
    }
  }, [search, seatingTabs, filteredTableData])



  const renderTables = (tabId) => {
    const list = (filteredTableData[tabId] || []).map(table => ({ ...table, _tabId: tabId }));



    return list.map(table => {
      const tabKey = table._tabId;
      const isSelected = (selectedTables?.[tabKey] || []).includes(table.id);
      const info = occupiedTables[table.id];
      const isOccupied = !!info;
      const isSpecialTab = ["person-tab", "tabs-tab", "takeaway-tab"].includes(tabKey);
     
      const customerName = info?.customerName;
      const orderName = info?.orderName;
      const tableName = table.name || `${table.tableNumber}`;

      // const fullText = [customerName, orderName, tableName].filter(Boolean).join(" - ");
      // const displayText = customerName || orderName || tableName || "";
      const fullText = [orderName, tableName].filter(Boolean).join(" - ");
      const displayText = orderName || customerName || tableName || "";
      return (
        <div
          key={table.id}
          title={fullText}
          className={`tableCol${isSelected ? ' selected' : ''}${isOccupied ? ' reserved' : ''}`}
        >

          <span
            className="infoTrigger"
            onClick={() =>
              handleInfoClick(table.id, `${table.tableNumber}`)
            }
          >
            <svg width="28" height="28" className="infoIcon" style={{width:"28px"}}> 
              <use href="/images/sprite.svg#iconInfo" />
            </svg>
          </span>


          <input
            type="radio"
            className="tableInput"
            name={`tableInput-${tabId}`}
            checked={isSelected}
            onChange={() => handleTableSelect(tabId, table.id)}
          />


          {isOccupied && !isSpecialTab && (
             <span style={{position:"absolute", top:"14px",  left:"50%", transform: "translateX(-50%)", whiteSpace:"nowrap"}}>
              {getElapsed(info.started)}
            </span>
          )}
          <svg width="24" height="24" className="checkSquareIcon">
            <use href="/images/sprite.svg#iconCheckSquare" />
          </svg>

          <label>


            <span className="tableWithText">
              <img
                className="tableImg"
                src={table.img}
                alt={`${table.tableNumber}`}
              />

              {!isSpecialTab && (
                <ul className="tableText">
                  <li class="tableNumber">
                    <svg width="21" height="14" class="tableNumberIcon">
                      <use xlinkHref="./images/sprite.svg#iconTableNumber"></use></svg>
                    <span>#{table.tableNumber}</span>
                  </li>
                  <li className="personNumber">
                    <svg width="11" height="10" className="personIcon">
                      <use href="/images/sprite.svg#iconPersons" />
                    </svg>
                    <span>{table.personNumber}</span>
                  </li>
                </ul>
              )}
            </span>
          </label>


                    {isOccupied && !isSpecialTab && (
            <span 
            className="cTimeCol" 
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              printKitchenDocket(table.id, 'c');
            }}
            style={{ cursor: 'pointer', zIndex:15 }}
            >
              <svg
                width="40"
                height="40"
                
                viewBox="0 0 25 25"
      
              >
                <rect width="28" height="28" fill="white" />
                <path fillRule="evenodd" clipRule="evenodd" d="M12.9622 5.45703V7.4872C12.6467 7.45039 12.3257 7.43122 12.0001 7.43122C11.6744 7.43122 11.3534 7.45039 11.0379 7.4872V5.45703H12.9622Z" fill="black" />
                <path fillRule="evenodd" clipRule="evenodd" d="M12 7.86328C16.022 7.86327 19.3079 10.9289 19.4277 14.7408L15.2918 14.7408C14.8632 13.3934 13.5497 12.41 12 12.41C10.4502 12.41 9.1368 13.3935 8.70818 14.7408L4.57215 14.7408C4.69188 10.9288 7.97797 7.86328 12 7.86328ZM15.2644 10.5984C16.0849 11.0355 16.757 11.7245 17.1568 12.5382C17.1952 12.6165 17.2767 12.6624 17.3616 12.6624C17.3938 12.6624 17.4264 12.6559 17.4575 12.642C17.5705 12.5914 17.6192 12.4629 17.5662 12.355C17.1266 11.46 16.3874 10.7023 15.4847 10.2214C15.3757 10.1634 15.2379 10.2006 15.1771 10.3047C15.1162 10.4088 15.1553 10.5402 15.2644 10.5984ZM7.88702 11.5261C7.96528 11.5261 8.04136 11.4873 8.0832 11.4179C8.55292 10.639 9.28311 10.0063 10.1392 9.63624C10.253 9.58707 10.3035 9.4592 10.2519 9.35063C10.2004 9.24205 10.0665 9.19386 9.95271 9.24306C9.01103 9.65 8.20791 10.346 7.69132 11.2026C7.62902 11.3058 7.66622 11.4378 7.77445 11.4973C7.81 11.5168 7.84878 11.5261 7.88702 11.5261Z" fill="black" />
                <path fillRule="evenodd" clipRule="evenodd" d="M8.56045 15.6926C8.56045 16.2143 8.68872 16.7077 8.9165 17.1461L2.67627 17.1461L3.43267 15.1719H4.34292H8.60388C8.57543 15.3415 8.56045 15.5154 8.56045 15.6926Z" fill="black" />
                <path fillRule="evenodd" clipRule="evenodd" d="M11.739 13.9741C11.2557 14.0762 10.9371 14.4744 10.9556 14.9673C10.9601 15.0864 11.0659 15.1796 11.1897 15.1752C11.3145 15.1709 11.412 15.0709 11.4075 14.9518C11.3937 14.5862 11.6551 14.4192 11.8832 14.3874C12.1285 14.3531 12.4549 14.4591 12.5673 14.8708C12.6171 15.0532 12.5796 15.251 11.9731 15.4614C11.7166 15.5505 10.8887 15.8981 10.9867 16.6363C11.0276 17.0386 11.4003 17.3199 11.8088 17.4084L11.8088 17.7651C11.8088 17.8842 11.91 17.9809 12.0349 17.9809C12.1598 17.9809 12.261 17.8842 12.261 17.7651L12.261 17.4237C12.6506 17.3598 13.0517 17.0752 13.0452 16.4389C13.044 16.3204 12.943 16.2252 12.8191 16.2252C12.8184 16.2251 12.8177 16.2252 12.8169 16.2252C12.6921 16.2263 12.5918 16.3239 12.593 16.4431C12.5978 16.9124 12.2954 16.9969 12.1147 17.0056C11.8211 17.0198 11.4604 16.8425 11.4366 16.5929L11.4357 16.5844C11.3777 16.1619 11.9507 15.9285 12.1277 15.8671C12.8422 15.6191 13.1374 15.2473 13.0049 14.7619C12.8766 14.2921 12.5392 14.0464 12.1912 13.9737L12.1912 13.6218C12.1912 13.5026 12.09 13.406 11.9652 13.406C11.8403 13.406 11.7391 13.5026 11.7391 13.6218L11.739 13.9741ZM12 12.8418C13.6473 12.8418 14.9874 14.121 14.9874 15.6934C14.9874 17.2658 13.6473 18.5448 12 18.5448C10.3528 18.5448 9.01266 17.2658 9.01266 15.6934C9.01265 14.121 10.3528 12.8418 12 12.8418Z" fill="black" />
                <path fillRule="evenodd" clipRule="evenodd" d="M15.3962 15.1719H19.6572H20.5674L21.3237 17.1461H15.0836C15.3113 16.7077 15.4396 16.2142 15.4396 15.6926C15.4396 15.5154 15.4247 15.3415 15.3962 15.1719Z" fill="black" />

              </svg>


            </span>
          )}

          {isSpecialTab && (
            <div
              className="cTimeCol"
              
              style={{
                textAlign: "center",
                left: "50%",
                transform: "translateX(-50%)"
              }}
            >
              {displayText}
            </div>
          )}

          {isOccupied && !isSpecialTab && (
            <span className="tblIcons"  style={{    zIndex: 15}}>

              <svg
                width="40"
                height="40"
                viewBox="0 0 25 25"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  printKitchenDocket(table.id, 'd');
                }}
              >
                <rect width="28" height="28" fill="white" />
                <g clipPath="url(#clip0_1259_14537)">
                  <path d="M17.3861 15.6072C17.3129 15.6504 17.2358 15.6859 17.1562 15.714V19.4995C17.4674 19.4122 17.9451 19.2385 18.3876 18.8747C19.1258 18.2679 19.5 17.3779 19.5 16.2292V13.4668C19.3333 13.7391 19.1438 14.0023 18.9317 14.2544C18.2293 15.0891 17.5209 15.5278 17.3861 15.6072Z" fill="black" />
                  <path d="M14.959 15.7904C14.959 16.0647 14.8957 16.3244 14.7832 16.556L16.2773 17.0763V15.715L14.959 15.2559V15.7904Z" fill="black" />
                  <path d="M11.4434 14.0314L4.5 11.6133V12.9746L11.4434 15.3927V14.0314Z" fill="black" />
                  <path d="M13.2012 17.5491C12.4299 17.5491 11.7735 17.0495 11.5373 16.3571L4.5 13.9062V15.0588C4.5 15.2458 4.61833 15.4123 4.7949 15.4738L16.2773 19.4727V18.0078L14.1529 17.268C13.8783 17.4455 13.5517 17.5491 13.2012 17.5491Z" fill="black" />
                  <path d="M9.53906 8.16209C9.53906 9.21212 10.3933 10.0664 11.4434 10.0664C12.4934 10.0664 13.3477 9.21212 13.3477 8.16209C13.3477 7.25146 12.705 6.48851 11.8495 6.30186C11.7266 5.5748 11.268 4.9465 10.5917 4.60832L10.468 4.54648C10.251 4.43796 9.98698 4.52594 9.87841 4.743C9.76986 4.96006 9.85784 5.22406 10.0749 5.33257L10.1987 5.39442C10.5798 5.58502 10.85 5.92346 10.9551 6.32152C10.1409 6.53767 9.53906 7.28078 9.53906 8.16209Z" fill="black" />
                  <path d="M19.5 10.3711C19.5 10.2497 19.4709 7.3824 16.9399 5.89092C16.8284 5.82524 16.6937 5.81203 16.5716 5.85474C16.2023 5.984 15.1754 6.34166 13.8676 6.7969C14.096 7.20067 14.2266 7.66675 14.2266 8.16281C14.2266 9.69747 12.978 10.946 11.4434 10.946C10.0565 10.946 8.90347 9.92639 8.69417 8.59746C6.89681 9.22309 5.36792 9.75553 4.79429 9.9563C4.61789 10.018 4.49985 10.1846 4.5 10.3714C4.50015 10.5583 4.61845 10.7246 4.7949 10.7861L12.3223 13.4075V15.791C12.3223 16.2764 12.7158 16.6699 13.2012 16.6699C13.6866 16.6699 14.0801 16.2764 14.0801 15.791V14.0197L16.5722 14.8876C16.6192 14.904 16.6681 14.9121 16.7168 14.9121C16.7944 14.9121 16.8715 14.8915 16.9399 14.8512C17.0445 14.7896 19.5 13.3088 19.5 10.3711ZM14.8125 10.6641C14.5698 10.6641 14.373 10.4673 14.373 10.2246C14.373 9.98191 14.5698 9.78515 14.8125 9.78515C15.0552 9.78515 15.252 9.98191 15.252 10.2246C15.252 10.4673 15.0552 10.6641 14.8125 10.6641ZM15.6914 12.7148C15.4487 12.7148 15.252 12.5181 15.252 12.2754C15.252 12.0327 15.4487 11.8359 15.6914 11.8359C15.9341 11.8359 16.1309 12.0327 16.1309 12.2754C16.1309 12.5181 15.9341 12.7148 15.6914 12.7148ZM16.5703 8.90625C16.3276 8.90625 16.1309 8.70949 16.1309 8.46679C16.1309 8.2241 16.3276 8.02734 16.5703 8.02734C16.813 8.02734 17.0098 8.2241 17.0098 8.46679C17.0098 8.70949 16.813 8.90625 16.5703 8.90625ZM17.1562 10.957C16.9136 10.957 16.7168 10.7603 16.7168 10.5176C16.7168 10.2749 16.9136 10.0781 17.1562 10.0781C17.3989 10.0781 17.5957 10.2749 17.5957 10.5176C17.5957 10.7603 17.3989 10.957 17.1562 10.957Z" fill="black" />
                </g>
                <defs>
                  <clipPath id="clip0_1259_14537">
                    <rect width="15" height="15" fill="white" transform="translate(4.5 4.5)" />
                  </clipPath>
                </defs>
              </svg>
            </span>
          )}


        </div>
      );
    });
  };



  return (
    <MainLayout>
      <div className="pageContent selectTablePage">
        <div className="container">
          <div className="seatingArea">
            <div className="tabStyle" role="tablist">

              <Swiper
                className="seatingSwiper"
                slidesPerView="auto"
                spaceBetween={50}
                speed={500}
                navigation={{ nextEl: '.sTabNext', prevEl: '.sTabPrev' }}
                modules={[Navigation]}
              >
                {seatingTabs.map(tab => (
                  <SwiperSlide key={tab.id} className="w-auto">
                    <button
                      className={`nav-link${activeTab === tab.id ? ' active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      <svg width="40" height="40"><use href={tab.icon} /></svg>
                      {tab.label}
                    </button>
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="tabHeaderRow">
                <form className="formStyle searchStyle tabSearchInput">
                  <svg width="17" height="18" className='searchIcon'>
                    <use href="/images/sprite.svg#iconSearch" />
                  </svg>
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="form-control"
                    placeholder="Search a Table Number"
                    value={search}
                    onChange={handleSearch}
                  />
                </form>
              </div>
              <div className="tab-content">
                {seatingTabs.map(tab => (
                  <div
                    key={tab.id}
                    className={`tab-pane fade${activeTab === tab.id ? ' show active' : ''}`}
                  >


                    <div className="d-flex flex-wrap gap-4 gap-xl-5 justify-content-center mx-auto col-xl-10">
                      {renderTables(tab.id)}
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      </div>

      <FinalizeOrderCartItemModal
        isOpen={cartModal.open}
        tableId={cartModal.tableId}
        tableName={cartModal.tableName}
        onClose={() => setCartModal({ open: false })}
      />
    </MainLayout>
  );
};

export default FinalizeOrder;
